import { useContext, useMemo, useState } from "react";

// Third party
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { Alert } from "@mui/material";

// style
import "./GenerateInvoice.scss";
import { IInvoiceTableRow, usePostService, useScannedDetails } from "../../../hooks";
import { GenerateInvoiceToolbar } from "./GenerateInvoiceToolbar";
import { API, EInvoicingType } from "../../../constants";
import { GlobalContextData } from "../../../context";
import { useNavigate } from "react-router-dom";
import { MGButton, MGDatePicker } from "../../../components";
import { addDays } from "../../../utils/date.utils";


export const GenerateInvoice = () => {
  const { state } = useContext(GlobalContextData);
  const navigate = useNavigate();

  const [selectedData, setSelectedData] = useState<Array<any>>([]);
  const [searchParams, setSearchParams] = useState("");
  const { postData, isSubmitting } = usePostService();
  const [apiError, setApiError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState<Date>(() => addDays(new Date(), -7));
  const [endDate, setEndDate] = useState<Date>(() => new Date());
  const [appliedStartDate, setAppliedStartDate] = useState<Date>(() => addDays(new Date(), -7));
  const [appliedEndDate, setAppliedEndDate] = useState<Date>(() => new Date());
  const [dateError, setDateError] = useState<string | null>(null);

  const { rows, isRowsLoading, refetchData, error: fetchError } = useScannedDetails(
    appliedStartDate,
    appliedEndDate
  );

  const normalizedFetchErrorMessage = useMemo(() => {
    if (!fetchError) return null;
    if (typeof fetchError === "string") return fetchError;
    if (typeof (fetchError as any)?.message === "string") return (fetchError as any).message;
    if (typeof (fetchError as any)?.response?.data?.message === "string") {
      return (fetchError as any).response.data.message;
    }
    if (typeof (fetchError as any)?.err?.message === "string") return (fetchError as any).err.message;
    return "Unable to load scan details. Please try again.";
  }, [fetchError]);

  const handleRowSelectionModelChange = (data: any) => {
    setSelectedData(data);
    setApiError(null);
  };

  const columnHeaderData = useMemo(() => {
    return [
      {
        field: "tmId",
        headerName: "Crew Id",
        flex: 1,
      },
      {
        field: "greeterName",
        headerName: "Greeter",
        flex: 1,
      },
      {
        field: "scannedByGreeterOn",
        headerName: "Scanned on",
        flex: 1.5,
        valueGetter: (value: any, row: any) => {
          return row.scannedByGreeterOn;
        },
      },
      {
        field: "requestType",
        headerName: "Request Type",
        flex: 1,
      },
    ];
  }, []);

  const handleSearchParam = (text: string) => {
    setSearchParams(text);
  };

  const applyDateFilters = () => {
    setApiError(null);
    if (!startDate || !endDate) {
      setDateError("Please select both From and To dates.");
      return;
    }
    if (endDate < startDate) {
      setDateError("The 'To' date cannot be earlier than the 'From' date.");
      return;
    }
    setDateError(null);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const isDateFilterChanged = useMemo(() => {
    const sameStart = dayjs(startDate).startOf("day").isSame(dayjs(appliedStartDate).startOf("day"));
    const sameEnd = dayjs(endDate).startOf("day").isSame(dayjs(appliedEndDate).startOf("day"));
    return !sameStart || !sameEnd;
  }, [startDate, endDate, appliedStartDate, appliedEndDate]);

  const rowData = useMemo(() => {
    if (searchParams) {
      return rows.filter((item: IInvoiceTableRow) => item.tmId?.includes(searchParams));
    }
    return rows;
  }, [rows, searchParams]);

  const rowsToExport = useMemo(() => {
    if (selectedData.length > 0) {
      return rowData.filter((r: any) => selectedData.includes(r.id));
    }
    return rowData;
  }, [rowData, selectedData]);

  const handleGenerate = async () => {
    setApiError(null);
    const payload = {
      invoiceRequest: [
        {
          vendorId: state.userInfo["cognito:username"],
          status: EInvoicingType.DRAFT,
          amountDue: "5001",
          tmIds: selectedData,
          serviceCostDetails: [
            // {
            //   postDate: "",
            //   description: "",
            //   comment: "",
            //   amount: "",
            //   id: uuidv4(),
            // },
          ],
        },
      ],
    };
    try {
      const res = await postData(
        `${API.INVOICE.CREATE}`,
        {
          ...payload,
        },
        "POST"
      );
      if (res.data[0].id) {
        navigate(`/invoicing/${res.data[0].id}`);
      }
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : typeof (err as any)?.message === "string"
            ? (err as any).message
            : typeof (err as any)?.response?.data?.message === "string"
              ? (err as any).response.data.message
              : "Unable to generate invoice. Please try again.";
      setApiError(msg);
    }
  };

  const maxSelectableEndDate = addDays(startDate, 31);

  return (
    <section className="invoicing-details-container">
      <div className="generate-invoice-filters">
        {dateError && (
          <Alert
            severity="error"
            onClose={() => setDateError(null)}
            variant="filled"
            sx={{ marginBottom: 2 }}
          >
            {dateError}
          </Alert>
        )}
        {normalizedFetchErrorMessage && (
          <Alert
            severity="error"
            variant="filled"
            sx={{ marginBottom: 2 }}
          >
            {normalizedFetchErrorMessage}
          </Alert>
        )}
        {apiError && (
          <Alert
            severity="error"
            onClose={() => setApiError(null)}
            variant="filled"
            sx={{ marginBottom: 2 }}
          >
            {apiError}
          </Alert>
        )}
        <div className="generate-invoice-filter-row">
          <MGDatePicker
            containerClassName="date-box"
            label="From"
            dateFormat="MM/dd/yyyy"
            selected={startDate}
            onChange={(date) => date && setStartDate(date)}
          />
          <MGDatePicker
            containerClassName="date-box mar-left"
            label="To"
            dateFormat="MM/dd/yyyy"
            selected={endDate}
            minDate={startDate}
            maxDate={maxSelectableEndDate}
            onChange={(date) => date && setEndDate(date)}
          />
          <MGButton
            size="large"
            className="apply-btn"
            disabled={!(startDate && endDate) || !isDateFilterChanged}
            onClick={applyDateFilters}
          >
            Apply
          </MGButton>
        </div>
      </div>
      <DataGrid
        rows={rowData || []}
        sx={{ height: rows.length === 0 ? 200 : "auto", marginTop: 2 }}
        columns={columnHeaderData || []}
        loading={isRowsLoading}
        slots={{
          toolbar: GenerateInvoiceToolbar,
        }}
        slotProps={
          {
            toolbar: {
              rows: rows,
              rowsToExport,
              searchParams,
              handleSearchParam: handleSearchParam,
              isSelecteItemForGenerate: selectedData.length === 0,
              handleGenerate: handleGenerate,
              isSubmitting: isSubmitting,
            },
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "skeleton",
            },
          } as any
        }
        checkboxSelection
        onRowSelectionModelChange={handleRowSelectionModelChange}
        rowSelectionModel={selectedData}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        hideFooter={[].length === 0}
      />
    </section>
  );
};
